import { useEffect, useRef, useState } from 'react';
import { X, Plus, Check, Upload, Download, Paperclip, Eye, Trash2, FileText, Scale } from 'lucide-react';
import { api } from '@/lib/api';
import { AttachmentThumb, AttachmentPreviewModal } from '@/components/AttachmentPreview';
import CaseWorkflow from '@/components/CaseWorkflow';

const TABS = ['Overview', 'Workflow', 'Timeline', 'Notes', 'Witnesses', 'Attachments', 'Reminders', 'Audit'];

export default function CaseDetail({ caseId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [noteText, setNoteText] = useState('');
  const [witness, setWitness] = useState({ name: '', role: '', statement: '', credibility: 'Medium', confidential: false });
  const [reminder, setReminder] = useState({ description: '', due_date: '' });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewAtt, setPreviewAtt] = useState(null);
  const fileInputRef = useRef(null);

  const load = () => api(`/cases/${caseId}`).then(setData);
  const loadAttachments = () => api(`/cases/${caseId}/attachments`).then(setAttachments);
  useEffect(() => { load(); loadAttachments(); /* eslint-disable-next-line */ }, [caseId]);

  const updateStatus = async (status) => {
    await api(`/cases/${caseId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
    await load(); onChanged?.();
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    await api(`/cases/${caseId}/notes`, { method: 'POST', body: JSON.stringify({ content: noteText }) });
    setNoteText(''); load();
  };
  const addWitness = async () => {
    if (!witness.name || !witness.statement) return;
    await api(`/cases/${caseId}/witnesses`, { method: 'POST', body: JSON.stringify(witness) });
    setWitness({ name: '', role: '', statement: '', credibility: 'Medium', confidential: false });
    load();
  };
  const addReminder = async () => {
    if (!reminder.description || !reminder.due_date) return;
    await api('/reminders', { method: 'POST', body: JSON.stringify({ ...reminder, case_id: caseId }) });
    setReminder({ description: '', due_date: '' }); load(); onChanged?.();
  };
  const completeReminder = async (id) => {
    await api(`/reminders/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'done' }) });
    load();
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploadError('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/attachments`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Upload failed');
      await loadAttachments();
      load();
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadAttachment = async (att) => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${att.id}/download`, {
      credentials: 'include',
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = att.original_filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const deleteAttachment = async (att) => {
    if (!window.confirm(`Delete "${att.original_filename}"? This will keep an audit record.`)) return;
    await api(`/attachments/${att.id}`, { method: 'DELETE' });
    await loadAttachments();
    load();
  };

  const humanSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const downloadReport = async () => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/report.pdf`, { credentials: 'include' });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `FairWay-${caseId}-report.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    load();
  };

  const downloadLegalPack = async () => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/legal-pack.pdf`, { credentials: 'include' });
    if (!res.ok) return;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `FairWay-${caseId}-legal-pack.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    load();
  };

  if (!data) {
    return (
      <div className="drawer-overlay" onClick={onClose}>
        <aside className="drawer" onClick={(e) => e.stopPropagation()}><p className="muted">Loading case…</p></aside>
      </div>
    );
  }
  const c = data.case;

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()} data-testid="case-detail-drawer">
        <div className="drawer-head">
          <div>
            <span className="eyebrow">{c.case_type.toUpperCase()} · {c.id}</span>
            <h2>{c.subject}</h2>
            <div className="drawer-meta">
              <span className={`severity ${c.severity.toLowerCase().replace(/\s+/g, '-')}`}>{c.severity}</span>
              <span className={`status ${c.status.toLowerCase().replaceAll(' ', '-')}`}>{c.status}</span>
              <span className="muted">{c.vessel}</span>
            </div>
          </div>
          <button data-testid="drawer-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="drawer-subactions">
          <button data-testid="download-report-button" className="secondary-btn" onClick={downloadReport}>
            <FileText size={14} /> Download signed PDF report
          </button>
          <button data-testid="download-legal-pack-button" className="ghost-btn" onClick={downloadLegalPack}>
            <Scale size={13} /> Legal pack (full file)
          </button>
        </div>
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t} data-testid={`tab-${t.toLowerCase()}`} className={tab === t ? 'tab active' : 'tab'} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>
        <div className="drawer-body">
          {tab === 'Overview' && (
            <div className="stack">
              <div className="field"><label>Summary</label><p>{c.summary}</p></div>
              <div className="row-two">
                <div className="field"><label>Category</label><p>{c.category}</p></div>
                <div className="field"><label>Opened</label><p>{c.opened}</p></div>
              </div>
              <div className="field"><label>Next action</label><p>{c.next_action}</p></div>
              <div className="field">
                <label>Move status to</label>
                <div className="chip-row">
                  {['Open', 'Under Investigation', 'Hearing Scheduled', 'Resolved', 'Appealed'].map((s) => (
                    <button key={s} data-testid={`status-${s.replaceAll(' ', '-').toLowerCase()}`} className={c.status === s ? 'chip active' : 'chip'} onClick={() => updateStatus(s)}>{s}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {tab === 'Workflow' && (
            <CaseWorkflow caseId={caseId} onChanged={() => { load(); onChanged?.(); }} />
          )}
          {tab === 'Timeline' && (
            <div className="stack">
              {(() => {
                const events = [];
                for (const n of data.notes) {
                  events.push({ id: `note-${n.id}`, at: n.created_at, kind: 'note', title: 'Note added', by: n.author_name, body: n.content });
                }
                for (const w of data.witnesses) {
                  events.push({ id: `w-${w.id}`, at: w.created_at, kind: 'witness', title: `Witness interviewed · ${w.name}`, by: w.interviewed_by, body: `${w.role || 'Witness'} · credibility ${w.credibility}${w.confidential ? ' · confidential' : ''}\n${w.statement}` });
                }
                for (const a of (data.attachments || [])) {
                  events.push({ id: `a-${a.id}`, at: a.created_at, kind: 'attachment', title: `Attached ${a.original_filename}`, by: a.uploaded_by });
                }
                for (const r of data.reminders) {
                  events.push({ id: `r-${r.id}`, at: r.created_at, kind: 'reminder', title: `Follow-up scheduled · ${r.description}`, body: `Due ${r.due_date} · ${r.status}` });
                }
                for (const ev of data.audit) {
                  if (['updated', 'created', 'attachment_deleted'].includes(ev.action)) {
                    events.push({ id: `au-${ev.id}`, at: ev.timestamp, kind: 'audit', title: ev.action === 'created' ? 'Case opened' : ev.action === 'attachment_deleted' ? `Attachment removed · ${ev.details?.filename || ''}` : `Case updated`, by: ev.actor_name, body: ev.action === 'updated' ? Object.entries(ev.details || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') : '' });
                  }
                }
                events.sort((a, b) => (b.at || '').localeCompare(a.at || ''));
                if (events.length === 0) return <p className="muted">No activity yet on this case.</p>;
                return events.map((e) => (
                  <div className={`timeline-item timeline-${e.kind}`} key={e.id} data-testid={`timeline-${e.id}`}>
                    <div className={`timeline-dot ${e.kind}`} />
                    <div className="timeline-body">
                      <div className="timeline-head">
                        <strong>{e.title}</strong>
                        <span className="muted">{new Date(e.at).toLocaleString()}{e.by ? ` · ${e.by}` : ''}</span>
                      </div>
                      {e.body && <p className="timeline-text">{e.body}</p>}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
          {tab === 'Notes' && (
            <div className="stack">
              <div className="composer">
                <textarea data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a case note (visible to HR)…" />
                <button data-testid="add-note-button" className="primary-btn" onClick={addNote}><Plus size={14} /> Add note</button>
              </div>
              {data.notes.length === 0 && <p className="muted">No notes yet.</p>}
              {data.notes.map((n) => (
                <div className="note-item" key={n.id} data-testid={`note-${n.id}`}>
                  <div className="note-meta"><strong>{n.author_name}</strong><span>{new Date(n.created_at).toLocaleString()}</span></div>
                  <p>{n.content}</p>
                </div>
              ))}
            </div>
          )}
          {tab === 'Witnesses' && (
            <div className="stack">
              <div className="composer grid">
                <input data-testid="witness-name" placeholder="Witness name" value={witness.name} onChange={(e) => setWitness({ ...witness, name: e.target.value })} />
                <input data-testid="witness-role" placeholder="Role / position" value={witness.role} onChange={(e) => setWitness({ ...witness, role: e.target.value })} />
                <select data-testid="witness-credibility" value={witness.credibility} onChange={(e) => setWitness({ ...witness, credibility: e.target.value })}>
                  <option>High</option><option>Medium</option><option>Low</option>
                </select>
                <label className="checkbox">
                  <input type="checkbox" data-testid="witness-confidential" checked={witness.confidential} onChange={(e) => setWitness({ ...witness, confidential: e.target.checked })} />
                  Confidential
                </label>
                <textarea data-testid="witness-statement" placeholder="Statement / interview notes…" value={witness.statement} onChange={(e) => setWitness({ ...witness, statement: e.target.value })} />
                <button data-testid="add-witness-button" className="primary-btn" onClick={addWitness}><Plus size={14} /> Record interview</button>
              </div>
              {data.witnesses.length === 0 && <p className="muted">No witness records yet.</p>}
              {data.witnesses.map((w) => (
                <div className="note-item" key={w.id} data-testid={`witness-${w.id}`}>
                  <div className="note-meta"><strong>{w.name}</strong><span>{w.role || 'Interview'} · credibility {w.credibility}{w.confidential ? ' · confidential' : ''}</span></div>
                  <p>{w.statement}</p>
                </div>
              ))}
            </div>
          )}
          {tab === 'Attachments' && (
            <div className="stack">
              <div className="composer">
                <input
                  ref={fileInputRef}
                  type="file"
                  data-testid="attachment-input"
                  style={{ display: 'none' }}
                  onChange={(e) => uploadFile(e.target.files[0])}
                />
                <button
                  data-testid="upload-attachment-button"
                  className="primary-btn"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Upload size={14} /> {uploading ? 'Uploading…' : 'Upload transcript, photo or PDF'}
                </button>
                <small className="muted">Max 25MB. Files are stored privately per case.</small>
                {uploadError && <div data-testid="upload-error" className="error">{uploadError}</div>}
              </div>
              {attachments.length === 0 && <p className="muted">No files attached yet.</p>}
              {attachments.map((a) => {
                const isImage = (a.content_type || '').startsWith('image/');
                const isPdf = a.content_type === 'application/pdf';
                const canPreview = isImage || isPdf;
                return (
                  <div className="attachment-item" key={a.id} data-testid={`attachment-${a.id}`}>
                    {isImage
                      ? <AttachmentThumb attachment={a} />
                      : <div className="thumb-placeholder"><Paperclip size={20} /></div>}
                    <div className="attachment-body">
                      <div className="note-meta">
                        <strong>{a.original_filename}</strong>
                        <span>{humanSize(a.size || 0)} · {a.uploaded_by} · {new Date(a.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="attachment-actions">
                        {canPreview && (
                          <button className="secondary-btn" data-testid={`preview-attachment-${a.id}`} onClick={() => setPreviewAtt(a)}>
                            <Eye size={12} /> Preview
                          </button>
                        )}
                        <button className="secondary-btn" data-testid={`download-attachment-${a.id}`} onClick={() => downloadAttachment(a)}>
                          <Download size={12} /> Download
                        </button>
                        <button className="danger-btn" data-testid={`delete-attachment-${a.id}`} onClick={() => deleteAttachment(a)}>
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {tab === 'Reminders' && (
            <div className="stack">
              <div className="composer grid">
                <input data-testid="reminder-description" placeholder="What needs following up?" value={reminder.description} onChange={(e) => setReminder({ ...reminder, description: e.target.value })} />
                <input data-testid="reminder-due" type="date" value={reminder.due_date} onChange={(e) => setReminder({ ...reminder, due_date: e.target.value })} />
                <button data-testid="add-reminder-button" className="primary-btn" onClick={addReminder}><Plus size={14} /> Add follow-up</button>
              </div>
              {data.reminders.length === 0 && <p className="muted">No follow-ups scheduled.</p>}
              {data.reminders.map((r) => (
                <div className="note-item" key={r.id} data-testid={`case-reminder-${r.id}`}>
                  <div className="note-meta">
                    <strong>{r.description}</strong>
                    <span>due {r.due_date} · {r.status}</span>
                  </div>
                  {r.status === 'open' && (
                    <button className="secondary-btn" data-testid={`complete-reminder-${r.id}`} onClick={() => completeReminder(r.id)}>
                      <Check size={12} /> Mark done
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {tab === 'Audit' && (
            <div className="stack">
              {data.audit.length === 0 && <p className="muted">No audit events yet.</p>}
              {data.audit.map((a) => (
                <div className="note-item" key={a.id} data-testid={`audit-${a.id}`}>
                  <div className="note-meta">
                    <strong>{a.action.replaceAll('_', ' ')}</strong>
                    <span>{a.actor_name} · {new Date(a.timestamp).toLocaleString()}</span>
                  </div>
                  {a.details && Object.keys(a.details).length > 0 && (
                    <p className="muted mono">{JSON.stringify(a.details)}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
      {previewAtt && <AttachmentPreviewModal attachment={previewAtt} onClose={() => setPreviewAtt(null)} />}
    </div>
  );
}
